import Button from 'components/button/Button';
import { CheckboxInput } from 'components/form/CheckboxInput';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import instance from 'api';
import { endpoints } from 'endpoints.config';
import ServiceFeeTermsModal from './ServiceFeeTermsModal';
import { useBrandIdentity } from 'hooks/useBrandIdentity';

type Props = {
    onComplete: () => void;
    hasDDAProduct: boolean;
};

const fieldName = (name: string) => `terms.${name}`;

/**
 * Personal customer acknowledgements shown before Jumio consent.
 * No API call – all state is local. User must check all boxes to proceed to Jumio verification.
 */
export const PersonalAcknowledgementsBeforeJumio = ({ onComplete, hasDDAProduct }: Props) => {
    const [isServiceFeeModalOpen, setIsServiceFeeModalOpen] = useState(false);
    const { brandKey } = useBrandIdentity();
    // The Service Fee document/checkbox is specific to the 75 Pillars (jhglobal) brand.
    const isJhGlobal = brandKey === 'jhglobal';
    const initialValues = {
        terms: {
            bTaxAcknowledgement: false,
            ...(hasDDAProduct ? {} : { bFatcaAcknowledgement: false }),
            bConfirmTrueAndCorrect: false,
            bAcceptTerms: false,
            ...(isJhGlobal ? { bServiceFeeAcknowledgement: false } : {}),
        },
    };
    const companyName = localStorage?.getItem("companyName")

    return (
        <div className="ConsentCollection">
            <Formik
                initialValues={initialValues}
                onSubmit={async (values) => {
                    // Persist the acknowledgements so they aren't re-requested after a reload
                    // (QA Finding #6). Reuses updatecustomeruserterms — all fields are nullable,
                    // so sending only these 4 leaves the Jumio-consent flags untouched.
                    try {
                        await instance.post(endpoints.personalVerification.updateCustomerUserTerms, {
                            bTaxAcknowledgement: values.terms.bTaxAcknowledgement,
                            // DDA products don't collect FATCA — send true so it isn't read back as un-accepted.
                            bFatcaAcknowledgement: hasDDAProduct
                                ? true
                                : values.terms.bFatcaAcknowledgement,
                            bConfirmTrueAndCorrect: values.terms.bConfirmTrueAndCorrect,
                            bAcceptTerms: values.terms.bAcceptTerms,
                            ...(isJhGlobal
                                ? { bServiceFeeAcknowledgement: values.terms.bServiceFeeAcknowledgement }
                                : {}),
                        });
                    } catch {
                        // Don't block the user if persistence fails — they've already accepted here.
                    }
                    onComplete();
                }}
            >
                {({ values, handleSubmit, setFieldValue }) => {
                    // For DDA products, FATCA is not required, so exclude it from validation
                    const requiredTerms = {
                        bTaxAcknowledgement: values.terms.bTaxAcknowledgement,
                        ...(hasDDAProduct
                            ? {}
                            : { bFatcaAcknowledgement: values.terms.bFatcaAcknowledgement }),
                        bConfirmTrueAndCorrect: values.terms.bConfirmTrueAndCorrect,
                        bAcceptTerms: values.terms.bAcceptTerms,
                        ...(isJhGlobal
                            ? { bServiceFeeAcknowledgement: values.terms.bServiceFeeAcknowledgement }
                            : {}),
                    };
                    const allChecked = Object.values(requiredTerms).every(Boolean);

                    return (
                        <Form>
                            <h2>Terms and Acknowledgements</h2>
                            <p className="FormSubtitle">
                                Please review and accept the following terms and acknowledgements to
                                proceed with verification
                            </p>
                            <div className="FormBox">
                                <div className="FormLabel">
                                    <label>Tax Acknowledgement</label>
                                </div>
                                <div className="FormField ButtonCheckboxInput">
                                    <CheckboxInput
                                        name={fieldName('bTaxAcknowledgement')}
                                        label="I confirm that the entity applying for the account is up to date and compliant with all applicable tax laws and regulations for which the entity would be responsible."
                                    />
                                </div>
                            </div>
                            {!hasDDAProduct && (
                                <div className="FormBox">
                                    <div className="FormLabel">
                                        <label>FATCA Acknowledgement</label>
                                    </div>
                                    <div className="FormField ButtonCheckboxInput">
                                        <CheckboxInput
                                            name={fieldName('bFatcaAcknowledgement')}
                                            label="I do confirm that I am in full compliance with the Foreign Account Tax Compliance Act (FATCA)."
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="FormBox">
                                <div className="FormLabel">
                                    <label>Information Supplied is True and Correct</label>
                                </div>
                                <div className="FormField ButtonCheckboxInput">
                                    <CheckboxInput
                                        name={fieldName('bConfirmTrueAndCorrect')}
                                        label="I confirm that the information provided is true and correct. I will notify, in writing, any changes. Information provided in this questionnaire may be transferred to the tax administrator or to any other institution in accordance with international treaties or agreements, as well as the United States regarding automated exchange of information on financial accounts."
                                    />
                                </div>
                            </div>
                            <div className="FormBox">
                                <div className="FormLabel">
                                    <label>Accept Services of {companyName} {companyName?.toLowerCase() === "ibanera" ? "LLC" : ""}</label>
                                </div>
                                <div className="FormField ButtonCheckboxInput">
                                    <CheckboxInput
                                        name={fieldName('bAcceptTerms')}
                                        label={`I agree to use the services of ${companyName} ${companyName?.toLowerCase() === "ibanera" ? "LLC" : ""} only for legitimate purposes. I acknowledge that false or incorrect information provided could require reports to regulators or law enforcement resulting in termination of business relations.`}
                                    />
                                </div>
                            </div>
                            {isJhGlobal && (
                                <div className="FormBox">
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <div className="FormLabel">
                                            <label>Service Fee Acknowledgement</label>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsServiceFeeModalOpen(true)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                color: '#6366f1',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                            }}
                                        >
                                            Review Document
                                        </button>
                                    </div>
                                    <div className="FormField ButtonCheckboxInput">
                                        <CheckboxInput
                                            name={fieldName('bServiceFeeAcknowledgement')}
                                            label={
                                                <>
                                                    I understand and agree that each incoming wire
                                                    received through 75 Pillars is subject to a
                                                    Service Fee of twelve percent (12%) of the
                                                    transaction amount, calculated and applied as
                                                    described in the Terms &amp; Conditions.
                                                    <br />
                                                    <br />
                                                    E.g. If receiving a 10,000.00 USD wire to your
                                                    75 Pillars account, a 12% service fee plus an
                                                    additional 0.4% OTC fee will be charged — for
                                                    this example this fee amounts to 1,235.66 USD.
                                                    End user balance after the fee will be
                                                    8,729.34 USD.
                                                </>
                                            }
                                            checked={values.terms.bServiceFeeAcknowledgement}
                                            disabled
                                        />
                                    </div>
                                    {!values.terms.bServiceFeeAcknowledgement && (
                                        <p style={{ color: '#888', fontSize: 12, margin: '4px 0 0' }}>
                                            Please review and accept the document above to check this box.
                                        </p>
                                    )}
                                </div>
                            )}
                            <Button
                                type="submit"
                                variety="full"
                                disabled={!allChecked}
                                onClick={() => handleSubmit()}
                            >
                                Continue
                            </Button>
                            {isJhGlobal && isServiceFeeModalOpen && (
                                <ServiceFeeTermsModal
                                    close={() => setIsServiceFeeModalOpen(false)}
                                    onAccept={() => {
                                        setFieldValue('terms.bServiceFeeAcknowledgement', true);
                                        setIsServiceFeeModalOpen(false);
                                    }}
                                />
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
